import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/store";
import RightArrowIcon from "../../../../../../assets/svg/right-arrow-icon";
import Button from "../../../../../../components/Button/Button";
import TicketsByStatusChart from "./components/TicketsByStatusChart";
import TicketsBySystemChart from "./components/TicketsBySystemChart";
import SLAComplianceChart from "./components/SLAComplianceChart";
import TopIssueTypesList from "./components/TopIssueTypesList";
import TicketMetric from "./components/TicketMetric";
import {
  MetricItem,
  StageChartItem,
  STAGE_CHART_COLORS,
  STAGE_CHART_COLORS_DARK,
  DEFAULT_CHART_COLOR,
  PriorityChartItem,
  PRIORITY_CHART_CONFIG,
  DEFAULT_PRIORITY_CONFIG,
  SlaComplianceData,
  MONTH_NAMES,
} from "./utils/constants";
import PriorityDistributionChart from "./components/PriorityDistributionChart";
import ClosedTicketsPerSite from "./components/ClosedTicketsPerSite";
import TopTeamMemberPerformance from "./components/TopTeamMemberPerformance";
import TicketsClosedMonthly from "./components/TicketsClosedMonthly";
import MonthlyRaisedVsClosed from "./components/MonthlyRaisedVsClosed";
import { useNavigate } from "react-router-dom";
import {
  useGetTicketsByStageReportQuery,
  useGetTicketsByPriorityQuery,
  useGetTicketsBySlaComplianceQuery,
  useGetTicketsByCustomFieldQuery,
  useGetTicketsBySiteQuery,
  useGetTicketsByMonthQuery,
} from "../../../../../../redux/services/helpdeskApi";
import { SystemChartDataItem } from "./components/TicketsBySystemChart";
import { IssueTypeDataItem } from "./components/TopIssueTypesList";
import { SiteTicketData } from "./components/ClosedTicketsPerSite";
import { ClosedMonthData } from "./components/TicketsClosedMonthly";
import { RaisedVsClosedMonthData } from "./components/MonthlyRaisedVsClosed";
import {
  MetricShimmer,
  BarChartCardShimmer,
  SLAChartShimmer,
  PriorityChartShimmer,
  IssueListShimmer,
  SiteTableShimmer,
  WideBarChartShimmer,
} from "./components/TicketReportsShimmer";
import {
  StageReportItem,
  PriorityReportItem,
  SlaMetricItem,
  CustomFieldResponseItem,
  SiteMetricItem,
  MonthTicketItem,
} from "./utils/types";
import { getHelpdeskLabels } from "../../../../../../utils/constants/mappings";

export const TicketReports = () => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const navigate = useNavigate();
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const { data: stageReport, isLoading: stageLoading } =
    useGetTicketsByStageReportQuery({}, { refetchOnMountOrArgChange: true });
  const { data: priorityReport, isLoading: priorityLoading } =
    useGetTicketsByPriorityQuery({}, { refetchOnMountOrArgChange: true });
  const { data: slaReport, isLoading: slaLoading } =
    useGetTicketsBySlaComplianceQuery({}, { refetchOnMountOrArgChange: true });
  const { data: systemReport, isLoading: systemLoading } =
    useGetTicketsByCustomFieldQuery(
      { key: "system-name" },
      { refetchOnMountOrArgChange: true }
    );
  const { data: issueTypeReport, isLoading: issueTypeLoading } =
    useGetTicketsByCustomFieldQuery(
      { key: "issue-type" },
      { refetchOnMountOrArgChange: true }
    );
  const { data: siteReport, isLoading: siteLoading } = useGetTicketsBySiteQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const { data: monthReport, isLoading: monthLoading } =
    useGetTicketsByMonthQuery({}, { refetchOnMountOrArgChange: true });

  const metricsData: MetricItem[] = useMemo(() => {
    if (!stageReport?.stages?.length) return [];

    const totalItem: MetricItem = {
      label: `Total ${helpdeskLabels.ticketPlural}`,
      value: stageReport?.total_ticket_count ?? 0,
    };

    const stageItems: MetricItem[] = stageReport?.stages?.map(
      (stage: StageReportItem) => ({
        label: stage?.stage_title?.toUpperCase(),
        value: stage?.count,
        stageKey: stage?.stage_key,
        bgColor: stage?.secondary_hex_code,
      })
    );

    return [totalItem, ...stageItems];
  }, [stageReport]);

  const chartData: StageChartItem[] = useMemo(() => {
    if (!stageReport?.stages?.length) return [];

    const darkOverride = isDarkMode ? STAGE_CHART_COLORS_DARK : {};
    return stageReport?.stages
      ?.map((stage: StageReportItem) => ({
        status: stage?.stage_title?.toUpperCase(),
        value: stage?.count,
        color:
          darkOverride[stage?.stage_key]?.bg ||
          STAGE_CHART_COLORS[stage?.stage_key] ||
          DEFAULT_CHART_COLOR,
        textColor: darkOverride[stage?.stage_key]?.text,
        stageKey: stage?.stage_key,
      }))
      ?.sort((a: StageChartItem, b: StageChartItem) => b.value - a.value);
  }, [stageReport, isDarkMode]);

  const systemChartData: SystemChartDataItem[] = useMemo(() => {
    if (!systemReport?.ticket_response?.length) return [];

    return systemReport?.ticket_response
      ?.map((item: CustomFieldResponseItem) => ({
        system: item?.primary_name,
        key: item?.primary_key,
        value: item?.ticket_count,
      }))
      ?.sort(
        (a: SystemChartDataItem, b: SystemChartDataItem) => b.value - a.value
      );
  }, [systemReport]);

  const priorityData: PriorityChartItem[] = useMemo(() => {
    if (!priorityReport?.priorities?.length) return [];

    return priorityReport?.priorities?.map((p: PriorityReportItem) => {
      const config =
        PRIORITY_CHART_CONFIG[p?.priority] ?? DEFAULT_PRIORITY_CONFIG;
      return {
        id: p?.priority,
        label: config?.label,
        tickets: p?.count,
        value: p?.percentage,
        displayPercent: `${Math.round(p?.percentage)}%`,
        color: config?.color,
        rowBg: config?.rowBg,
        rowBgDk: config?.rowBgDk,
        flagColor: config?.flagColor,
        hasFlag: config?.hasFlag,
      };
    });
  }, [priorityReport]);

  const priorityTotal = priorityReport?.total_ticket_count ?? 0;

  const slaData: SlaComplianceData = useMemo(() => {
    if (!slaReport?.metrics?.length)
      return {
        withinSla: { count: 0, percentage: 0 },
        withoutSla: { count: 0, percentage: 0 },
        total: 0,
      };

    const onTime = slaReport?.metrics?.find(
      (m: SlaMetricItem) => m?.metric_name === "on_time"
    );
    const overdue = slaReport?.metrics?.find(
      (m: SlaMetricItem) => m?.metric_name === "overdue"
    );

    return {
      withinSla: {
        count: onTime?.count ?? 0,
        percentage: onTime?.percentage ?? 0,
      },
      withoutSla: {
        count: overdue?.count ?? 0,
        percentage: overdue?.percentage ?? 0,
      },
      total: slaReport?.total_ticket_count ?? 0,
    };
  }, [slaReport]);

  const issueTypesData: IssueTypeDataItem[] = useMemo(() => {
    if (!issueTypeReport?.ticket_response?.length) return [];

    return issueTypeReport?.ticket_response?.map(
      (item: CustomFieldResponseItem) => ({
        title: item?.primary_name,
        system: item?.secondary_name ?? "",
        count: item?.ticket_count,
      })
    );
  }, [issueTypeReport]);

  const siteData: SiteTicketData[] = useMemo(() => {
    if (!siteReport?.site_metrics?.length) return [];

    return siteReport?.site_metrics?.map((item: SiteMetricItem) => ({
      siteName: item?.site_name,
      totalTickets: item?.ticket_count,
      closedTickets: item?.total_closed_tickets,
    }));
  }, [siteReport]);

  const closedMonthlyData: ClosedMonthData[] = useMemo(() => {
    if (!monthReport?.closed_tickets?.length) return [];

    return monthReport?.closed_tickets?.map((item: MonthTicketItem) => ({
      month: MONTH_NAMES[item?.month - 1] ?? `M${item?.month}`,
      year: String(item?.year),
      value: item?.ticket_count,
    }));
  }, [monthReport]);

  const raisedVsClosedData: RaisedVsClosedMonthData[] = useMemo(() => {
    const openTickets = monthReport?.open_tickets as
      | MonthTicketItem[]
      | undefined;
    const closedTickets = monthReport?.closed_tickets as
      | MonthTicketItem[]
      | undefined;
    if (!openTickets?.length && !closedTickets?.length) return [];

    const allMonths = new Set<string>();
    openTickets?.forEach((item: MonthTicketItem) =>
      allMonths?.add(`${item.year}-${String(item.month).padStart(2, "0")}`)
    );
    closedTickets?.forEach((item: MonthTicketItem) =>
      allMonths?.add(`${item.year}-${String(item.month).padStart(2, "0")}`)
    );

    return Array.from(allMonths)
      .sort()
      .reverse()
      .map((key) => {
        const [yearStr, monthStr] = key?.split("-") ?? [];
        const monthNum = Number(monthStr ?? 0);
        const open = openTickets?.find(
          (i: MonthTicketItem) =>
            i?.month === monthNum && String(i?.year) === yearStr
        );
        const closed = closedTickets?.find(
          (i: MonthTicketItem) =>
            i?.month === monthNum && String(i?.year) === yearStr
        );
        return {
          month: MONTH_NAMES[monthNum - 1] ?? `M${monthNum}`,
          year: yearStr!,
          raised: open?.ticket_count ?? 0,
          closed: closed?.ticket_count ?? 0,
        };
      });
  }, [monthReport]);

  const chartCards: {
    key: string;
    isLoading: boolean;
    shimmer: React.ReactNode;
    component: React.ReactNode;
    colSpan?: number;
  }[] = [
    {
      key: "status",
      isLoading: stageLoading,
      shimmer: <BarChartCardShimmer />,
      component: <TicketsByStatusChart chartData={chartData} />,
    },
    {
      key: "system",
      isLoading: systemLoading,
      shimmer: <BarChartCardShimmer />,
      component: <TicketsBySystemChart data={systemChartData} />,
    },
    {
      key: "sla",
      isLoading: slaLoading,
      shimmer: <SLAChartShimmer />,
      component: <SLAComplianceChart slaData={slaData} />,
    },
    {
      key: "priority",
      isLoading: priorityLoading,
      shimmer: <PriorityChartShimmer />,
      component: (
        <PriorityDistributionChart
          priorityData={priorityData}
          totalTickets={priorityTotal}
        />
      ),
    },
    {
      key: "issues",
      isLoading: issueTypeLoading,
      shimmer: <IssueListShimmer />,
      component: <TopIssueTypesList data={issueTypesData} />,
    },
    {
      key: "sites",
      isLoading: siteLoading,
      shimmer: <SiteTableShimmer />,
      component: <ClosedTicketsPerSite data={siteData} />,
    },
    {
      key: "closed-monthly",
      isLoading: monthLoading,
      shimmer: <WideBarChartShimmer />,
      component: <TicketsClosedMonthly data={closedMonthlyData} />,
      colSpan: 2,
    },
    {
      key: "raised-vs-closed",
      isLoading: monthLoading,
      shimmer: <WideBarChartShimmer dualLegend />,
      component: <MonthlyRaisedVsClosed data={raisedVsClosedData} />,
      colSpan: 2,
    },
  ];

  return (
    <div className="flex flex-col gap-6 p-4">
      <div className="flex items-center gap-2.5">
        <Button
          iconLeft={true}
          Icon={RightArrowIcon}
          buttonContainerClass="w-fit"
          innerTextClass="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk leading-normal pb-[2px]"
          iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk rotate-180"
          type="button"
          onClick={() => navigate(-1)}
        />
        <div className="flex flex-col">
          <h2 className="text-base font-medium text-heading-text dark:text-heading-text-dk">
            {helpdeskLabels.ticketSingular} Reports & Analytics
          </h2>
          <h5 className="text-xs text-dark-gray dark:text-dark-gray-dk">
            Comprehensive report for Support {helpdeskLabels.ticketPlural}
          </h5>
        </div>
      </div>
      <div className="w-full grid grid-cols-[repeat(auto-fill,minmax(max(140px,calc((100%-40px)/6)),1fr))] gap-2 items-center">
        {stageLoading ? (
          <MetricShimmer />
        ) : (
          <TicketMetric metricsData={metricsData} />
        )}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {chartCards?.map(({ key, isLoading, shimmer, component, colSpan }) => (
          <div key={key} className={colSpan === 2 ? "xl:col-span-2" : ""}>
            {isLoading ? shimmer : component}
          </div>
        ))}
      </div>
    </div>
  );
};
